"use client";

import { useCommsPanel } from "./CommsProvider";
import { ResizeHandle } from "./ResizeHandle";
import { TimelineTab } from "./tabs/TimelineTab";
import { CopilotTab } from "./tabs/CopilotTab";
import { X, Pin, PinOff, MessageSquare, Bot } from "lucide-react";

export function CommsPanel() {
  const { isOpen, isPinned, activeLeadName, activeTab, panelWidth, closePanel, togglePin, setActiveTab } = useCommsPanel();

  if (!isOpen) return null;

  const tabs = [
    { key: "timeline" as const, label: "Timeline", icon: MessageSquare },
    { key: "copilot" as const, label: "Copilot", icon: Bot },
  ];

  const panel = (
    <div
      data-comms-panel
      className="relative flex flex-col border-l border-gray-200 bg-white h-full"
      style={{ width: isPinned ? panelWidth : panelWidth }}
    >
      <ResizeHandle />

      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-gray-900">{activeLeadName ?? "Lead"}</h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={togglePin}
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title={isPinned ? "Unpin panel" : "Pin panel"}
          >
            {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </button>
          <button
            onClick={closePanel}
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Close panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex border-b border-gray-200">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex flex-1 items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
              activeTab === key
                ? "border-b-2 border-[#1e5bb5] text-[#1e5bb5]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === "timeline" ? (
          <TimelineTab />
        ) : (
          <CopilotTab />
        )}
      </div>
    </div>
  );

  if (isPinned) {
    return panel;
  }

  // Unpinned: fixed overlay with backdrop
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={closePanel} />
      <div className="fixed inset-y-0 right-0 z-50" style={{ width: panelWidth }}>
        {panel}
      </div>
    </>
  );
}
